import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChevronRight, ExternalLink, Eye, Edit, Trash2 } from "lucide-react";

interface DynamicCardProps {
  title: string;
  description?: string;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  
  // Visual enhancements
  image?: string;
  badge?: {
    text: string;
    variant?: "default" | "secondary" | "destructive" | "outline";
    color?: string;
  };
  
  // Interactions
  onClick?: () => void;
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  
  // Animations
  hoverEffect?: "lift" | "glow" | "scale" | "tilt";
  pulseOnUpdate?: boolean;
  
  // States
  loading?: boolean;
  selected?: boolean;
  disabled?: boolean;
  
  className?: string;
}

export const DynamicCard: React.FC<DynamicCardProps> = ({
  title,
  description,
  children,
  footer,
  
  // Visual
  image,
  badge,
  
  // Interactions
  onClick,
  onView,
  onEdit,
  onDelete,
  
  // Animations
  hoverEffect = "lift",
  pulseOnUpdate = false,
  
  // States
  loading = false,
  selected = false,
  disabled = false,
  
  className
}) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const triggerUpdateAnimation = () => {
    if (pulseOnUpdate) {
      setIsUpdating(true);
      setTimeout(() => setIsUpdating(false), 1000);
    }
  };

  // استخدام تأثير التحديث عند تغيير البيانات
  React.useEffect(() => {
    triggerUpdateAnimation();
  }, [title, description, children]);

  const getHoverClasses = () => {
    switch (hoverEffect) {
      case 'lift':
        return 'hover:shadow-lg hover:-translate-y-1';
      case 'glow':
        return 'hover:shadow-xl hover:shadow-primary/20';
      case 'scale':
        return 'hover:scale-105';
      case 'tilt':
        return 'hover:rotate-1';
      default:
        return 'hover:shadow-md';
    }
  };

  const cardClasses = cn(
    "group relative overflow-hidden transition-all duration-300 cursor-pointer",
    getHoverClasses(),
    selected && "ring-2 ring-primary ring-offset-2",
    disabled && "opacity-50 cursor-not-allowed",
    isUpdating && "animate-pulse",
    loading && "animate-pulse",
    className
  );

  const hasActions = onView || onEdit || onDelete;

  return (
    <Card className={cardClasses} onClick={!disabled ? onClick : undefined}>
      {/* خلفية متحركة */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* الصورة */}
      {image && (
        <div className="relative overflow-hidden h-48">
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </div>
      )}

      <CardHeader className="relative">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold transition-colors group-hover:text-primary">
              {title}
            </CardTitle>
            {description && (
              <CardDescription className="mt-1">
                {description}
              </CardDescription>
            )}
          </div>
          
          {badge && (
            <Badge 
              variant={badge.variant || "secondary"}
              className="shrink-0"
              style={badge.color ? { 
                backgroundColor: `${badge.color}20`,
                color: badge.color,
                borderColor: `${badge.color}40`
              } : {}}
            >
              {badge.text}
            </Badge>
          )}
        </div>

        {/* مؤشر التفاعل */}
        {onClick && (
          <ChevronRight className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0" />
        )}
      </CardHeader>

      {children && (
        <CardContent className="relative">
          {loading ? (
            <div className="space-y-2">
              <div className="h-4 bg-muted animate-pulse rounded" />
              <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
            </div>
          ) : (
            children
          )}
        </CardContent>
      )}

      {(footer || hasActions) && (
        <CardFooter className="relative flex items-center justify-between pt-4">
          <div className="flex-1">
            {footer}
          </div>
          
          {hasActions && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {onView && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onView();
                  }}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              )}
              
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
              
              {onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              
              {onClick && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClick();
                  }}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </CardFooter>
      )}

      {/* مؤثر التحميل */}
      {loading && (
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            جاري التحميل...
          </div>
        </div>
      )}
    </Card>
  );
};

// مكونات متخصصة
export const ClientCard: React.FC<{
  client: {
    id: string;
    name_ar: string;
    name_en?: string;
    status: string;
    company?: string;
    mobile?: string;
  };
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
}> = ({ client, onView, onEdit, onDelete }) => {
  const statusColors: Record<string, string> = {
    'new': '#f59e0b',
    'in_progress': '#3b82f6',
    'completed': '#10b981',
    'cancelled': '#ef4444'
  };

  const statusLabels: Record<string, string> = {
    'new': 'جديد',
    'in_progress': 'قيد التنفيذ',
    'completed': 'مكتمل',
    'cancelled': 'ملغي'
  };

  return (
    <DynamicCard
      title={client.name_ar}
      description={client.name_en}
      badge={{
        text: statusLabels[client.status] || client.status,
        color: statusColors[client.status]
      }}
      hoverEffect="lift"
      pulseOnUpdate
      onView={() => onView?.(client.id)}
      onEdit={() => onEdit?.(client.id)}
      onDelete={() => onDelete?.(client.id)}
    >
      <div className="space-y-2 text-sm">
        {client.company && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>الشركة:</span>
            <span className="font-medium">{client.company}</span>
          </div>
        )}
        {client.mobile && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <span>الجوال:</span>
            <span className="font-medium">{client.mobile}</span>
          </div>
        )}
      </div>
    </DynamicCard>
  );
};